import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FADE_IN, RISE_IN } from "@/lib/motionPresets";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/apiClient";
import { trackLead } from "@/lib/tracking";
import { cheminDeVie, kua, parseBirthdate, isValidBirthdate } from "@/lib/tripleFusionCalc";
import { LANGS, detectLang, t } from "@/lib/diagnosticInauguralI18n";
import { log } from "@/lib/log";

const GOLD = "#C9A84C";
const inputCls = "w-full bg-transparent border border-white/20 focus:border-[#C9A84C] outline-none px-4 py-3 text-white placeholder-white/30 text-sm transition-colors";
const LANG_LABEL = { fr: "FR", en: "EN", es: "ES", pt: "PT" };

export const DiagnosticInauguralWidget = ({ compact = false, initialLang }) => {
  const [lang, setLang] = useState(initialLang || detectLang());
  const { ui, cv, kua: kuaTexts } = t(lang);
  const [form, setForm] = useState({ prenom: "", genre: "", birthdate: "", email: "", consent: false });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const b = parseBirthdate(form.birthdate);
    if (!form.prenom.trim() || !form.genre || !form.birthdate || !form.email.trim()) return setError(ui.errRequired);
    if (!isValidBirthdate(b)) return setError(ui.errDate);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError(ui.errEmail);
    if (!form.consent) return setError(ui.errConsent);
    setError("");
    const r = { cv: cheminDeVie(b), kua: kua(b.year, form.genre), prenom: form.prenom.trim() };
    setResult(r);
    setStatus("sending");
    try {
      const { data } = await api.post("/diagnostic-inaugural/lead", {
        prenom: r.prenom, email: form.email.trim().toLowerCase(), genre: form.genre,
        birthdate: `${String(b.day).padStart(2, "0")}/${String(b.month).padStart(2, "0")}/${b.year}`,
        chemin_de_vie: r.cv, kua: r.kua, langue: lang, consent: true,
      });
      trackLead({ content_name: "diagnostic_inaugural", eventID: data?.capi_event_id || undefined });
      setStatus("sent");
    } catch (err) {
      log.error("[diagnostic-inaugural] envoi échoué :", err);
      setStatus("error");
    }
  };

  const reset = () => { setResult(null); setStatus("idle"); setForm({ prenom: "", genre: "", birthdate: "", email: "", consent: false }); };

  return (
    <div className={`border ${compact ? "p-6 md:p-8" : "p-8 md:p-12"}`} style={{ borderColor: `${GOLD}55`, backgroundColor: "rgba(201,168,76,0.03)" }} data-testid="diagnostic-inaugural-widget">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: GOLD }}>{ui.kicker}</p>
          <h2 className={`font-serif text-white leading-tight ${compact ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"}`}>{ui.title}</h2>
        </div>
        <div className="flex gap-1 shrink-0" role="group" aria-label={ui.langLabel} data-testid="di-lang-switcher">
          {LANGS.map((l) => (
            <button key={l} type="button" onClick={() => setLang(l)} data-testid={`di-lang-${l}`} aria-pressed={lang === l}
              className={`px-2.5 py-1 text-[11px] tracking-[0.15em] border transition-colors ${lang === l ? "text-brand-ink" : "text-white/50 border-white/15 hover:text-white"}`}
              style={lang === l ? { backgroundColor: GOLD, borderColor: GOLD } : undefined}>{LANG_LABEL[l]}</button>
          ))}
        </div>
      </div>
      {!compact && <p className="text-white/60 leading-relaxed mb-8 max-w-2xl">{ui.intro}</p>}

      <AnimatePresence mode="wait">
        {!result ? (
          <motion.form key="form" {...FADE_IN} onSubmit={onSubmit} noValidate className="space-y-5" data-testid="di-form">
            <div className="grid md:grid-cols-2 gap-4">
              <input className={inputCls} placeholder={ui.prenom} value={form.prenom} onChange={set("prenom")} data-testid="di-prenom" aria-label={ui.prenom} />
              <input className={inputCls} type="email" placeholder={ui.email} value={form.email} onChange={set("email")} data-testid="di-email" aria-label={ui.email} />
              <div className="flex gap-3" role="radiogroup" aria-label={ui.genre}>
                {["homme", "femme"].map((g) => (
                  <label key={g} className={`flex-1 cursor-pointer border px-4 py-3 text-sm text-center transition-colors ${form.genre === g ? "text-brand-ink" : "text-white/70 border-white/20 hover:border-white/40"}`}
                    style={form.genre === g ? { backgroundColor: GOLD, borderColor: GOLD } : undefined}>
                    <input type="radio" name="di-genre" value={g} checked={form.genre === g} onChange={set("genre")} className="sr-only" data-testid={`di-genre-${g}`} />
                    {ui[g]}
                  </label>
                ))}
              </div>
              <input className={`${inputCls} [color-scheme:dark]`} type="date" value={form.birthdate} onChange={set("birthdate")} data-testid="di-birthdate" aria-label={ui.birthdate} />
            </div>
            <label className="flex items-start gap-3 text-xs text-white/55 leading-relaxed cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 accent-[#C9A84C]" data-testid="di-consent" />
              <span>{ui.consent}</span>
            </label>
            {error && <p className="text-sm text-red-300" data-testid="di-error">{error}</p>}
            <button type="submit" data-testid="di-submit"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold text-brand-ink transition-transform hover:scale-[1.02]"
              style={{ backgroundColor: GOLD }}>
              <Sparkles size={16} /> {ui.submit}
            </button>
          </motion.form>
        ) : (
          <motion.div key="result" {...RISE_IN} data-testid="di-result">
            <p className="text-white/50 text-sm mb-6">{ui.resultFor} <span className="text-white">{result.prenom}</span></p>
            <div className="grid md:grid-cols-2 gap-5">
              {[["cv", result.cv, cv[result.cv]], ["kua", result.kua, kuaTexts[result.kua]]].map(([key, num, text]) => (
                <div key={key} className="border border-white/10 p-6" data-testid={`di-${key}`}>
                  <p className="text-[11px] uppercase tracking-[0.25em] mb-3" style={{ color: GOLD }}>{ui[key]}</p>
                  <p className="font-serif text-6xl leading-none mb-4" style={{ color: GOLD }} data-testid={`di-${key}-value`}>{num}</p>
                  <p className="text-white/75 text-sm leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
            <p className="text-xs mt-5 text-white/45" data-testid="di-status">
              {status === "sending" && <span className="inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> {ui.sending}</span>}
              {status === "sent" && ui.emailNote}
              {status === "error" && ui.errSend}
            </p>
            <div className="mt-8 border-t border-white/10 pt-8">
              <p className="font-serif text-xl text-white mb-4">{ui.ctaLead}</p>
              <Link to="/analyse-de-precision" data-testid="di-cta-analyse"
                className="inline-flex items-center gap-3 px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold text-brand-ink transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: GOLD }}>
                {ui.cta} <ArrowRight size={16} />
              </Link>
              <button type="button" onClick={reset} className="block mt-5 text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors" data-testid="di-reset">{ui.again}</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
